import { apiFetch } from './auth-service';

export interface ItemDoc {
  _id?: string;
  title: string;
  type: 'text' | 'pdf' | 'video' | 'quiz';
  order: number;
  textContent: string;
  fileUrl: string;
  fileKey: string;
  fileName: string;
  fileSize: number;
  videoUrl: string;
  duration: number;
  passingScore: number;
  transcript?: string;
  transcriptionStatus?: 'none' | 'pending' | 'done' | 'failed' | 'skipped_too_large';
}

export interface ModuleDoc {
  _id?: string;
  title: string;
  description: string;
  order: number;
  items: ItemDoc[];
}

export interface CourseDoc {
  _id: string;
  title: string;
  description: string;
  thumbnailUrl: string;
  thumbnailKey: string;
  category: string;
  level: string;
  courseType: 'coursework' | 'complementary';
  price?: string;
  status: 'draft' | 'published' | 'archived';
  createdBy: string;
  modules: ModuleDoc[];
  createdAt: string;
  updatedAt: string;
}

export const getCourses = async (): Promise<CourseDoc[]> => {
  const r = await apiFetch.get<{ courses: CourseDoc[] }>('/courses');
  return r.courses;
};

export const createCourse = async (data: {
  title: string; category: string; description?: string; level?: string;
  courseType?: string; price?: string;
}): Promise<CourseDoc> => {
  const r = await apiFetch.post<{ course: CourseDoc }>('/courses', data);
  return r.course;
};

export const getCourseById = async (id: string): Promise<CourseDoc> => {
  const r = await apiFetch.get<{ course: CourseDoc }>(`/courses/${id}`);
  return r.course;
};

export const updateCourse = async (id: string, data: Partial<{
  title: string; description: string; thumbnailUrl: string; thumbnailKey: string;
  category: string; level: string; courseType: string; price: string;
  status: string; modules: ModuleDoc[];
}>): Promise<CourseDoc> => {
  const r = await apiFetch.put<{ course: CourseDoc }>(`/courses/${id}`, data);
  return r.course;
};

export const deleteCourse = async (id: string): Promise<void> => {
  await apiFetch.delete(`/courses/${id}`);
};

export const togglePublishCourse = async (id: string, currentStatus: string): Promise<CourseDoc> => {
  const nextStatus = currentStatus === 'published' ? 'draft' : 'published';
  const r = await apiFetch.post<{ course: CourseDoc }>(`/courses/${id}/status`, { status: nextStatus });
  return r.course;
};

export const archiveCourse = async (id: string): Promise<CourseDoc> => {
  const r = await apiFetch.post<{ course: CourseDoc }>(`/courses/${id}/status`, { status: 'archived' });
  return r.course;
};

export const unarchiveCourse = async (id: string): Promise<CourseDoc> => {
  const r = await apiFetch.post<{ course: CourseDoc }>(`/courses/${id}/status`, { status: 'draft' });
  return r.course;
};

export const presignCourseVideo = async (
  fileName: string, contentType: string
): Promise<{ presignedUrl: string; fileKey: string; fileUrl: string }> => {
  return apiFetch.post('/courses/videos/presign', { fileName, contentType });
};

export const deleteCourseVideo = async (fileKey: string): Promise<void> => {
  await apiFetch.delete(`/courses/videos/${encodeURIComponent(fileKey)}`);
};

export const triggerTranscription = async (
  courseId: string, moduleId: string, itemId: string
): Promise<void> => {
  await apiFetch.post(`/courses/${courseId}/modules/${moduleId}/items/${itemId}/transcribe`, {});
};

export const uploadCourseFile = async (
  courseId: string, file: File
): Promise<{ fileUrl: string; fileKey: string; fileName: string; fileSize: number }> => {
  const { presignedUrl, fileKey, fileUrl } = await apiFetch.post<{ presignedUrl: string; fileKey: string; fileUrl: string }>(
    '/courses/files/presign',
    { fileName: file.name, contentType: file.type || 'application/octet-stream', courseId }
  );
  const up = await fetch(presignedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  if (!up.ok) throw new Error('File upload to storage failed');
  return { fileUrl, fileKey, fileName: file.name, fileSize: file.size };
};

export const deleteCourseFile = async (_courseId: string, fileKey: string): Promise<void> => {
  if (!fileKey) return;
  await apiFetch.delete(`/courses/files/${encodeURIComponent(fileKey)}`).catch(() => {});
};
